import { IConsigned, IConsignedProduct, TypeProduct } from '../interfaces';
import { Chip, useTheme } from '@mui/material';
import { formatCurrency } from 'app/utils/normalizer';
import dayjs from 'dayjs';
import {
  GridContainer,
  InfoItem,
  InfoItemTitle,
  StyledAvatar,
  StyledCardHeader,
  StyledCheckboxOption,
  StyledContent,
  StyledContentWrapper,
  StyledInfoChip,
  StyledOption,
  StyledOptionHeader,
  StyledWarningMessage,
} from './ConsignedCard.styles';
import { formatCNPJ } from 'app/helpers';
import { CustomRadioInput } from 'app/components';
import { InfoOutlined } from '@mui/icons-material';
import FgtsQueryCheckbox from '../../components/FgtsQueryCheckbox/FgtsQueryCheckbox';
import { useContext } from 'react';
import { ApplicationRequestContext } from '../../ApplicationRequest';

type TConsignedCardProps = {
  consigned?: IConsigned;
  setConsignedConfig: (value?: IConsigned) => void;
  consignedProduct?: IConsignedProduct;
  setConsignedProduct: (value?: IConsignedProduct) => void;
};

const ConsignedCard = ({
  consigned,
  setConsignedConfig,
  setConsignedProduct,
  consignedProduct,
}: TConsignedCardProps) => {
  const theme = useTheme();
  const {
    fgtsQueryBalanceCheckId,
    setFgtsQueryBalanceCheckId,
    setFgtsPeriods,
    setFgtsValidationErrors,
  } = useContext(ApplicationRequestContext);

  const handleSelectProduct = (product: IConsignedProduct) => {
    setConsignedProduct(product);
    setConsignedConfig(consigned);

    if (
      fgtsQueryBalanceCheckId &&
      fgtsQueryBalanceCheckId !== product.balance_check_id
    ) {
      setFgtsQueryBalanceCheckId(undefined);
      setFgtsPeriods(undefined);
      setFgtsValidationErrors(false);
    }
  };

  return (
    <StyledContentWrapper>
      {consigned?.products?.map((product, key) => {
        const isLoanAuthorized: boolean =
          (product?.authorization?.details?.filter(
            (item) => item.type_product === TypeProduct.LOAN,
          )[0]?.authorized &&
            !!product.available_balance) ||
          false;
        const isSelected =
          product?.balance_check_id === consignedProduct?.balance_check_id;

        return (
          <div key={key}>
            <StyledOption isSelected={isSelected && !!isLoanAuthorized}>
              <StyledCardHeader>
                <StyledOptionHeader>
                  {consigned?.employer?.name || 'Nome indisponível'}
                </StyledOptionHeader>
                <StyledInfoChip>
                  <Chip
                    variant="outlined"
                    color={isLoanAuthorized ? 'success' : 'warning'}
                    label={
                      <StyledInfoChip>
                        <StyledAvatar
                          color={
                            isLoanAuthorized
                              ? (theme.palette.brand.success[500] as string)
                              : (theme.palette.brand.attention[500] as string)
                          }
                        />
                        {isLoanAuthorized ? 'Elegível' : 'Inelegível'}
                      </StyledInfoChip>
                    }
                  />
                  <StyledCheckboxOption>
                    <CustomRadioInput
                      isChecked={isSelected}
                      handleClick={() => handleSelectProduct(product)}
                      hasMargin
                      disabled={!isLoanAuthorized}
                    />
                  </StyledCheckboxOption>
                </StyledInfoChip>
              </StyledCardHeader>
              {consigned.meta_data.unauthorization_reason?.description && (
                <StyledWarningMessage>
                  <InfoOutlined
                    style={{
                      marginRight: '0.6rem',
                      color: theme.palette.brand.attention[700],
                    }}
                  />
                  <span>
                    Tomador inelegível:{' '}
                    {consigned.meta_data?.unauthorization_reason?.description ||
                      'Não foi possível informar o motivo.'}
                  </span>
                </StyledWarningMessage>
              )}
              <GridContainer>
                {consigned?.employer?.code && (
                  <StyledContent>
                    <InfoItemTitle>Inscrição empregador</InfoItemTitle>
                    <InfoItem>
                      {consigned?.employer?.code || 'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.employer?.document && (
                  <StyledContent>
                    <InfoItemTitle>Nº de inscrição</InfoItemTitle>
                    <InfoItem>
                      {formatCNPJ(consigned?.employer?.document || '') ||
                        'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.job_code?.description && (
                  <StyledContent>
                    <InfoItemTitle>Categoria de trabalho</InfoItemTitle>
                    <InfoItem>
                      {`${consigned?.meta_data?.job_code?.code || '0'} - ${
                        consigned?.meta_data?.job_code?.description ||
                        'Não informado'
                      }`}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.link_type_description && (
                  <StyledContent>
                    <InfoItemTitle>Categoria de trabalho</InfoItemTitle>
                    <InfoItem>
                      {`${consigned?.meta_data?.link_type_code || '0'} - ${
                        consigned?.meta_data?.link_type_description ||
                        'Não informado'
                      }`}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.functional_status_description && (
                  <StyledContent>
                    <InfoItemTitle>Status</InfoItemTitle>
                    <InfoItem>
                      {`${
                        consigned?.meta_data?.functional_status_code || '0'
                      } - ${
                        consigned?.meta_data?.functional_status_description ||
                        'Não informado'
                      }`}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.employee?.code && (
                  <StyledContent>
                    <InfoItemTitle>Matrícula do tomador</InfoItemTitle>
                    <InfoItem>
                      {consigned?.employee?.code || 'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.admission_date && (
                  <StyledContent>
                    <InfoItemTitle>Data admissão</InfoItemTitle>
                    <InfoItem>
                      {consigned?.meta_data?.admission_date
                        ? dayjs(consigned?.meta_data?.admission_date).format(
                            'DD/MM/YYYY',
                          )
                        : 'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.employer_activity_start_date && (
                  <StyledContent>
                    <InfoItemTitle>Início ativ. de empregador</InfoItemTitle>
                    <InfoItem>
                      {consigned?.meta_data?.employer_activity_start_date
                        ? dayjs(
                            consigned?.meta_data?.employer_activity_start_date,
                          ).format('DD/MM/YYYY')
                        : 'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.classification_description && (
                  <StyledContent>
                    <InfoItemTitle>Classificação</InfoItemTitle>
                    <InfoItem>
                      {consigned?.meta_data?.classification_description ||
                        'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.prior_notice_date && (
                  <StyledContent>
                    <InfoItemTitle>Aviso prévio</InfoItemTitle>
                    <InfoItem>
                      {dayjs(consigned?.meta_data?.prior_notice_date).format(
                        'DD/MM/YYYY',
                      )}{' '}
                      -{' '}
                      {dayjs(
                        consigned?.meta_data?.prior_notice_end_date,
                      ).format('DD/MM/YYYY') || 'xx/xx/xxxx'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.prior_notice_date && (
                  <StyledContent>
                    <InfoItemTitle>Afastamento</InfoItemTitle>
                    <InfoItem>
                      {dayjs(consigned?.meta_data?.prior_notice_date).format(
                        'DD/MM/YYYY',
                      )}{' '}
                      -{' '}
                      {dayjs(
                        consigned?.meta_data?.prior_notice_end_date,
                      ).format('DD/MM/YYYY') || 'xx/xx/xxxx'}
                    </InfoItem>
                  </StyledContent>
                )}
                {!!consigned?.meta_data?.total_earnings && (
                  <StyledContent>
                    <InfoItemTitle>Valor total vencimentos</InfoItemTitle>
                    <InfoItem>
                      {formatCurrency(
                        parseFloat(consigned.meta_data?.total_earnings),
                      )}
                    </InfoItem>
                  </StyledContent>
                )}
                {!!consigned?.meta_data?.base_margin_value && (
                  <StyledContent>
                    <InfoItemTitle>Valor base da margem</InfoItemTitle>
                    <InfoItem>
                      {formatCurrency(
                        parseFloat(consigned.meta_data?.base_margin_value),
                      )}
                    </InfoItem>
                  </StyledContent>
                )}

                <StyledContent>
                  <InfoItemTitle>Contratos ativos/suspensos</InfoItemTitle>
                  <InfoItem>
                    {consigned.meta_data?.amount_loans_assets_suspended || 0}
                  </InfoItem>
                </StyledContent>

                <StyledContent>
                  <InfoItemTitle>Margem disp. por parcela</InfoItemTitle>
                  <InfoItem>
                    {formatCurrency(product?.available_balance)}
                  </InfoItem>
                </StyledContent>
                {!!consigned?.meta_data?.termination_date && (
                  <StyledContent>
                    <InfoItemTitle>Data desligamento</InfoItemTitle>
                    <InfoItem>
                      {consigned?.meta_data?.termination_date
                        ? dayjs(consigned?.meta_data?.termination_date).format(
                            'DD/MM/YYYY',
                          )
                        : 'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                {consigned?.meta_data?.termination_reason_code && (
                  <StyledContent>
                    <InfoItemTitle>Motivo do desligamento</InfoItemTitle>
                    <InfoItem>
                      {consigned?.meta_data?.termination_reason_code ||
                        'Não informado'}
                    </InfoItem>
                  </StyledContent>
                )}
                <StyledContent>
                  <InfoItemTitle>Verba Rescisória Disponível</InfoItemTitle>
                  <InfoItem>
                    {consigned.meta_data?.guarantee_info?.has_guarantees
                      ? `${
                          consigned?.meta_data?.guarantee_info
                            ?.available_rescission_allowance_percent || '-'
                        }%`
                      : '0%'}
                  </InfoItem>
                </StyledContent>
              </GridContainer>
              <FgtsQueryCheckbox
                balanceCheckId={product.balance_check_id}
                onEnable={() => handleSelectProduct(product)}
              />

              {!!consigned.meta_data.alerts?.length && (
                <StyledContent>
                  <InfoItemTitle>Alerta</InfoItemTitle>
                  <InfoItem>
                    {consigned.meta_data.alerts?.map((alert) => (
                      <p key={alert.event_id}>
                        {alert.alert_type?.code || '-'} -{' '}
                        {alert?.alert_type?.description || '-'}
                      </p>
                    ))}
                  </InfoItem>
                </StyledContent>
              )}
            </StyledOption>
          </div>
        );
      })}
    </StyledContentWrapper>
  );
};

export default ConsignedCard;
